// Profile.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaIdCard,
    FaWallet,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaGlobe,
    FaShieldAlt,
    FaEdit,
    FaCamera,
    FaKey,
    FaQrcode,
    FaCopy,
    FaShare,
    FaStar,
    FaUserCheck,
    FaUserCog,
    FaMobileAlt,
    FaBuilding,
    FaMapPin,
    FaNetworkWired
} from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from '../utils/api';

function Profile() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('profile');
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [kycData, setKycData] = useState(null);

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const result = await apiCall("admin-profile", {}, true);

            if (result.status === true) {
                setUser(result.profile);
                setKycData(result.kyc_data);
            } else {
                toast.error(result.message || 'Failed to load profile');
            }
        } catch (error) {
            toast.error('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Convert timestamp to readable date
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(parseInt(timestamp) * 1000);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get role label
    const getRoleLabel = (role) => {
        const roles = {
            '1': 'Super Admin',
            '2': 'Admin',
            '3': 'Manager',
            '4': 'User',
            '5': 'Sub-User',
            '6': 'Agent',
            '7': 'Viewer'
        };
        return roles[role] || 'User';
    };

    // Get status label
    const getStatusLabel = (status) => {
        const statuses = {
            '1': 'Active',
            '2': 'Inactive',
            '3': 'Suspended',
            '4': 'Pending',
            '5': 'Blocked',
            '6': 'Deleted'
        };
        return statuses[status] || 'Unknown';
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            '1': 'bg-green-100 text-green-800 border-green-200',
            '2': 'bg-gray-100 text-gray-800 border-gray-200',
            '3': 'bg-red-100 text-red-800 border-red-200',
            '4': 'bg-yellow-100 text-yellow-800 border-yellow-200',
            '5': 'bg-orange-100 text-orange-800 border-orange-200',
            '6': 'bg-red-100 text-red-800 border-red-200'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    // Get role color
    const getRoleColor = (role) => {
        const colors = {
            '1': 'bg-purple-100 text-purple-800 border-purple-200',
            '2': 'bg-blue-100 text-blue-800 border-blue-200',
            '3': 'bg-indigo-100 text-indigo-800 border-indigo-200',
            '4': 'bg-green-100 text-green-800 border-green-200',
            '5': 'bg-teal-100 text-teal-800 border-teal-200',
            '6': 'bg-orange-100 text-orange-800 border-orange-200',
            '7': 'bg-gray-100 text-gray-800 border-gray-200'
        };
        return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Copy to clipboard
    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    // Verification Badge Component
    const VerificationBadge = ({ verified, label }) => (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${verified ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {verified ? (
                <FaCheckCircle className="text-green-500" />
            ) : (
                <FaTimesCircle className="text-red-500" />
            )}
            <span className="text-sm font-medium">{label}</span>
        </div>
    );

    // Info Row Component
    const InfoRow = ({ icon: Icon, label, value, copyable, copyLabel }) => (
        <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-3 rounded-lg transition-colors">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                <Icon className="text-sm" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 font-medium">{label}</p>
                <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-sm font-medium text-gray-900 break-all">{value || 'N/A'}</p>
                    {copyable && value && (
                        <button
                            onClick={() => copyToClipboard(value, copyLabel || label)}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                        >
                            <FaCopy className="text-xs" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    // Loading Component with centered spinner
    const LoadingSpinner = () => (
        <div className="bg-gray-50 min-h-screen">
            <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
            <div className="main-content lg:ml-[72px]">
                <Header currentPage={currentPage} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="flex justify-center items-center h-[calc(100vh-200px)]">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
                        <p className="text-gray-500 text-sm font-medium">Loading profile...</p>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                            <p className="text-sm text-gray-600">View and manage your profile information</p>
                        </div>
                        <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                            <FaEdit />
                            Edit Profile
                        </button>
                    </div>

                    {/* Profile Overview Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                        <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                {/* Avatar */}
                                <div className="relative">
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                                        {user?.name?.charAt(0) || 'U'}
                                    </div>
                                    <button className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full border-2 border-white hover:bg-blue-700 transition-colors">
                                        <FaCamera className="text-xs" />
                                    </button>
                                </div>

                                {/* User Info */}
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h2 className="text-2xl font-bold text-gray-900">{user?.name || 'N/A'}</h2>
                                        <span className={`px-3 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(user?.status)}`}>
                                            {getStatusLabel(user?.status)}
                                        </span>
                                        <span className={`px-3 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(user?.role)}`}>
                                            {getRoleLabel(user?.role)}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 mt-1">
                                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                            <FaEnvelope className="text-gray-400 text-xs" />
                                            <span>{user?.email || 'N/A'}</span>
                                            {user?.email_verification === '1' && (
                                                <FaCheckCircle className="text-green-500 text-xs" />
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                            <FaPhone className="text-gray-400 text-xs" />
                                            <span>{user?.mobile || 'N/A'}</span>
                                            {user?.mobile_verification === '1' && (
                                                <FaCheckCircle className="text-green-500 text-xs" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-gray-500">
                                        <div className="flex items-center gap-1.5">
                                            <FaCalendarAlt className="text-xs" />
                                            <span>Joined: {user?.date || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <FaClock className="text-xs" />
                                            <span>Time: {formatTimestamp(user?.time)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Referral Code */}
                                {user?.refer_code && (
                                    <div className="flex flex-col items-start sm:items-end gap-1 bg-white px-4 py-2 rounded-lg border border-gray-200">
                                        <span className="text-xs text-gray-500 font-medium">Referral Code</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold text-blue-600">{user?.refer_code}</span>
                                            <button
                                                onClick={() => copyToClipboard(user?.refer_code, 'Referral code')}
                                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                            >
                                                <FaCopy />
                                            </button>
                                            <button className="text-gray-400 hover:text-blue-600 transition-colors">
                                                <FaShare />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Balance Card */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-green-700 font-medium">Total Balance</p>
                                        <p className="text-2xl font-bold text-green-800">{formatCurrency(user?.balance)}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                                        <FaWallet className="text-green-700 text-xl" />
                                    </div>
                                </div>
                                <p className="text-xs text-green-600 mt-1">Last updated: {formatTimestamp(user?.last_balance_update)}</p>
                            </div>

                            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-blue-700 font-medium">Plan</p>
                                        <p className="text-2xl font-bold text-blue-800">Plan #{user?.plan_id || 'N/A'}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                                        <FaStar className="text-blue-700 text-xl" />
                                    </div>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">Active Subscription</p>
                            </div>

                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-purple-700 font-medium">User ID</p>
                                        <p className="text-2xl font-bold text-purple-800">#{user?.user_id || 'N/A'}</p>
                                    </div>
                                    <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                                        <FaUserCheck className="text-purple-700 text-xl" />
                                    </div>
                                </div>
                                <p className="text-xs text-purple-600 mt-1">Registered User</p>
                            </div>
                        </div>
                    </div>

                    {/* Verifications */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <FaShieldAlt className="text-blue-600" />
                                Verifications
                            </h3>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <VerificationBadge
                                    verified={user?.mobile_verification === '1'}
                                    label="Mobile"
                                />
                                <VerificationBadge
                                    verified={user?.email_verification === '1'}
                                    label="Email"
                                />
                                <VerificationBadge
                                    verified={user?.kyc_verification === '1'}
                                    label="KYC"
                                />
                                <VerificationBadge
                                    verified={user?.pan_verification === '1'}
                                    label="PAN"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Detailed Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Personal Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FaUserCog className="text-blue-600" />
                                    Personal Information
                                </h3>
                            </div>
                            <div className="p-4">
                                <InfoRow icon={FaUser} label="Full Name" value={user?.name} />
                                <InfoRow icon={FaEnvelope} label="Email" value={user?.email} copyable />
                                <InfoRow icon={FaPhone} label="Mobile" value={user?.mobile} copyable />
                                <InfoRow icon={FaIdCard} label="User ID" value={`#${user?.user_id}`} />
                                <InfoRow icon={FaCalendarAlt} label="Date" value={user?.date} />
                                <InfoRow icon={FaClock} label="Time" value={formatTimestamp(user?.time)} />
                            </div>
                        </div>

                        {/* Account Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FaNetworkWired className="text-blue-600" />
                                    Account Information
                                </h3>
                            </div>
                            <div className="p-4">
                                <InfoRow icon={FaUser} label="Role" value={getRoleLabel(user?.role)} />
                                <InfoRow icon={FaShieldAlt} label="Status" value={getStatusLabel(user?.status)} />
                                <InfoRow icon={FaQrcode} label="Referral Code" value={user?.refer_code} copyable copyLabel="Referral code" />
                                <InfoRow icon={FaUserCheck} label="Referred By" value={user?.refer_by ? `User #${user?.refer_by}` : 'None'} />
                                <InfoRow icon={FaUserCog} label="Agent ID" value={user?.agent_id ? `#${user?.agent_id}` : 'None'} />
                                <InfoRow icon={FaBuilding} label="App ID" value={`#${user?.app_id}`} />
                            </div>
                        </div>

                        {/* KYC Information */}
                        {kycData && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <FaIdCard className="text-blue-600" />
                                        KYC Information
                                    </h3>
                                </div>
                                <div className="p-4">
                                    <InfoRow icon={FaUser} label="Full Name" value={kycData?.aadhaar_name || kycData?.pan_name || 'N/A'} />
                                    <InfoRow icon={FaIdCard} label="Aadhaar Number" value={kycData?.aadhaar || 'N/A'} />
                                    <InfoRow icon={FaUser} label="Aadhaar Name" value={kycData?.aadhaar_name || 'N/A'} />
                                    <InfoRow icon={FaUser} label="Father's Name" value={kycData?.aadhaar_father || kycData?.pan_father || 'N/A'} />
                                    <InfoRow icon={FaCalendarAlt} label="Date of Birth" value={kycData?.aadhaar_dob || kycData?.pan_dob || 'N/A'} />
                                    <InfoRow icon={FaMapMarkerAlt} label="Address" value={kycData?.address || 'N/A'} />
                                    <InfoRow icon={FaMapPin} label="PIN Code" value={kycData?.aadhaar_pin || 'N/A'} />
                                    <InfoRow icon={FaGlobe} label="State" value={kycData?.aadhaar_state || 'N/A'} />
                                    <InfoRow icon={FaGlobe} label="District" value={kycData?.aadhaar_district || 'N/A'} />
                                    <InfoRow icon={FaUser} label="Gender" value={kycData?.aadhaar_gender || 'N/A'} />
                                </div>
                            </div>
                        )}

                        {/* Location Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FaMapMarkerAlt className="text-blue-600" />
                                    Location Information
                                </h3>
                            </div>
                            <div className="p-4">
                                <InfoRow icon={FaMapMarkerAlt} label="Current Location" value={user?.location || 'Not available'} />
                                <InfoRow icon={FaGlobe} label="IP Address" value={user?.ip_address || user?.ip || 'Not available'} copyable />
                                <InfoRow icon={FaMapPin} label="Last Location" value={user?.last_location || 'Not available'} />
                                <InfoRow icon={FaGlobe} label="Last IP" value={user?.last_ip_address || 'Not available'} copyable />
                                <InfoRow icon={FaMobileAlt} label="Device IP" value={user?.ip || 'Not available'} />
                            </div>
                        </div>

                        {/* Additional Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FaKey className="text-blue-600" />
                                    Additional Information
                                </h3>
                            </div>
                            <div className="p-4">
                                <InfoRow icon={FaWallet} label="Balance" value={formatCurrency(user?.balance)} />
                                <InfoRow icon={FaCalendarAlt} label="Last Balance Update" value={formatTimestamp(user?.last_balance_update)} />
                                <InfoRow icon={FaClock} label="Member Since" value={`${user?.date || 'N/A'} ${formatTimestamp(user?.time)}`} />
                                <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-3 rounded-lg transition-colors">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                                        <FaShieldAlt className="text-sm" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-500 font-medium">Security</p>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <FaCheckCircle className="mr-1" /> Secure
                                            </span>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Token Active
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="top-right" />
        </div>
    );
}

export default Profile;